import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import PushPin from '@mui/icons-material/PushPin'
import PushPinOutlined from '@mui/icons-material/PushPinOutlined'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { PieChart } from '@mui/x-charts/PieChart'
import { ScatterChart } from '@mui/x-charts/ScatterChart'
import { RadarChart } from '@mui/x-charts/RadarChart'
import { Gauge } from '@mui/x-charts/Gauge'
import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import { Unstable_FunnelChart as FunnelChart } from '@mui/x-charts-pro/FunnelChart'
import { Heatmap } from '@mui/x-charts-pro/Heatmap'
import {
  BRAND_NAVY,
  BRAND_RED,
  LIGHT_STATUS,
  TOUCH_MIN,
} from 'core/theme.tokens'
import type { AssistantChartData, AssistantChartType } from './useAssistantChat'
import { ALL_CHART_TYPES, CHART_TYPE_LABELS, chartOptionLabel } from './assistantToolPrefs'

const SERIES_COLORS = [
  LIGHT_STATUS.info.fg,
  BRAND_RED,
  LIGHT_STATUS.success.fg,
  LIGHT_STATUS.purple.fg,
  LIGHT_STATUS.inProgress.fg,
  BRAND_NAVY,
]

function formatNumber(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function plotSeries(chart: AssistantChartData): {
  categories: string[]
  series: Array<{ label: string; data: number[] }>
} {
  const categories = chart.categories?.length
    ? chart.categories
    : chart.slices?.map(slice => slice.label) || []
  const series = chart.series?.length
    ? chart.series
    : chart.slices?.length
      ? [{ label: chart.yLabel || 'Value', data: chart.slices.map(slice => slice.value) }]
      : []
  return { categories, series }
}

function initialType(chart: AssistantChartData): AssistantChartType {
  if (chart.chartType) return chart.chartType
  const allowed = chart.allowedTypes ?? []
  if (allowed.includes('bar')) return 'bar'
  return allowed[0] ?? 'bar'
}

function plotHeight(type: AssistantChartType, tall: boolean): number {
  if (type === 'sparkline') return tall ? 120 : 88
  if (type === 'gauge') return tall ? 220 : 180
  if (type === 'pie' || type === 'radar' || type === 'funnel' || type === 'heatmap') {
    return tall ? 360 : 260
  }
  return tall ? 340 : 220
}

interface AssistantChartCardProps {
  chart: AssistantChartData
  onPickType?: (type: AssistantChartType) => void
  /** Taller plot for the full-screen work stage. */
  tall?: boolean
  pinned?: boolean
  onTogglePin?: () => void
}

export const AssistantChartCard: React.FC<AssistantChartCardProps> = ({
  chart,
  tall = false,
  pinned = false,
  onTogglePin,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(320)
  const [activeType, setActiveType] = useState<AssistantChartType>(() => initialType(chart))

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => setWidth(Math.max(240, Math.floor(el.clientWidth)))
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setActiveType(initialType(chart))
  }, [chart])

  const height = plotHeight(activeType, tall)
  const alternatives = (chart.allowedTypes?.length ? chart.allowedTypes : ALL_CHART_TYPES).filter(
    type => type !== activeType,
  )

  return (
    <Box
      ref={containerRef}
      sx={{
        mt: { xs: 1, sm: 1.25 },
        p: { xs: 1, sm: 1.25 },
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 700, flex: 1, fontSize: { xs: '0.8125rem', sm: undefined } }}
        >
          {chart.title || 'Chart'}
        </Typography>
        <Chip
          size='small'
          label={CHART_TYPE_LABELS[activeType]}
          sx={{ height: { xs: 22, sm: undefined }, '& .MuiChip-label': { fontSize: { xs: '0.7rem', sm: undefined } } }}
        />
        {onTogglePin ? (
          <Tooltip title={pinned ? 'Unpin chart' : 'Pin chart'}>
            <IconButton
              size='small'
              aria-label={pinned ? 'Unpin chart' : 'Pin chart'}
              aria-pressed={pinned}
              color={pinned ? 'primary' : 'default'}
              onClick={onTogglePin}
              sx={{ width: 40, height: 40 }}
            >
              {pinned ? <PushPin fontSize='small' /> : <PushPinOutlined fontSize='small' />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <ChartPlot chart={chart} type={activeType} width={width} height={height} />
      {alternatives.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.75 }}>
            Visualize this in a
          </Typography>
          <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap>
            {alternatives.map(type => (
              <Chip
                key={type}
                label={chartOptionLabel(type)}
                clickable
                color='primary'
                variant='outlined'
                onClick={() => setActiveType(type)}
                sx={{
                  minHeight: { xs: 32, sm: TOUCH_MIN - 8 },
                  height: { xs: 32, sm: undefined },
                  fontSize: { xs: '0.75rem', sm: undefined },
                }}
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </Box>
  )
}

function ChartPlot({
  chart,
  type,
  width,
  height,
}: {
  chart: AssistantChartData
  type: AssistantChartType
  width: number
  height: number
}) {
  if (type === 'pie') return <PieView chart={chart} width={width} height={height} />
  if (type === 'bar') return <BarView chart={chart} width={width} height={height} />
  if (type === 'line') return <LineView chart={chart} width={width} height={height} />
  if (type === 'scatter') return <ScatterView chart={chart} width={width} height={height} />
  if (type === 'radar') return <RadarView chart={chart} width={width} height={height} />
  if (type === 'funnel') return <FunnelView chart={chart} width={width} height={height} />
  if (type === 'heatmap') return <HeatmapView chart={chart} width={width} height={height} />
  if (type === 'gauge') return <GaugeView chart={chart} width={width} height={height} />
  return <SparklineView chart={chart} width={width} height={height} />
}

function EmptyPlot({ label }: { label: string }) {
  return (
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
  )
}

function PieView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const data = useMemo(() => {
    if (chart.slices?.length) {
      return chart.slices.map((slice, index) => ({
        id: index,
        value: slice.value,
        label: slice.label,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
      }))
    }
    const { categories, series } = plotSeries(chart)
    if (!categories.length || !series[0]) return []
    return categories.map((label, index) => ({
      id: index,
      value: series[0].data[index] ?? 0,
      label,
      color: SERIES_COLORS[index % SERIES_COLORS.length],
    }))
  }, [chart])

  if (!data.length) return <EmptyPlot label='No pie data.' />

  return (
    <PieChart
      width={width}
      height={height}
      series={[
        {
          data,
          arcLabel: item => formatNumber(Number(item.value)),
          arcLabelMinAngle: 18,
          highlightScope: { fade: 'global', highlight: 'item' },
        },
      ]}
    />
  )
}

function BarView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const { categories, series } = plotSeries(chart)
  if (!categories.length || !series.length) return <EmptyPlot label='No bar data.' />

  return (
    <BarChart
      width={width}
      height={height}
      xAxis={[{ data: categories, scaleType: 'band', label: chart.xLabel }]}
      yAxis={[{ label: chart.yLabel, width: 48 }]}
      series={series.map((item, index) => ({
        data: item.data,
        label: item.label,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        valueFormatter: (value: number | null) => (value == null ? '—' : formatNumber(value)),
      }))}
      grid={{ horizontal: true }}
      margin={{ left: 8, right: 8, top: 16, bottom: chart.xLabel ? 40 : 24 }}
    />
  )
}

function LineView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const { categories, series } = plotSeries(chart)
  if (!categories.length || !series.length) return <EmptyPlot label='No line data.' />

  return (
    <LineChart
      width={width}
      height={height}
      xAxis={[{ data: categories, scaleType: 'point', label: chart.xLabel }]}
      yAxis={[{ label: chart.yLabel, width: 48 }]}
      series={series.map((item, index) => ({
        data: item.data,
        label: item.label,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        curve: 'linear',
        showMark: true,
        valueFormatter: (value: number | null) => (value == null ? '—' : formatNumber(value)),
      }))}
      grid={{ horizontal: true }}
      margin={{ left: 8, right: 8, top: 16, bottom: chart.xLabel ? 40 : 24 }}
    />
  )
}

function ScatterView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const { categories, series } = plotSeries(chart)
  if (!categories.length || !series.length) return <EmptyPlot label='No scatter data.' />

  return (
    <ScatterChart
      width={width}
      height={height}
      xAxis={[
        {
          label: chart.xLabel,
          min: -0.5,
          max: Math.max(categories.length - 0.5, 0.5),
          valueFormatter: (value: number | null) => {
            if (value == null || Number.isNaN(value)) return ''
            return categories[Math.round(value)] ?? formatNumber(value)
          },
        },
      ]}
      yAxis={[{ label: chart.yLabel }]}
      series={series.map((item, index) => ({
        label: item.label,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        data: categories.map((label, point) => ({
          x: point,
          y: item.data[point] ?? 0,
          id: `${index}-${label}`,
        })),
        valueFormatter: (value: { y: number } | null) =>
          value == null ? '—' : formatNumber(value.y),
      }))}
      grid={{ horizontal: true, vertical: true }}
    />
  )
}

function RadarView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const { categories, series } = plotSeries(chart)
  if (categories.length < 3 || !series.length) {
    return <EmptyPlot label='Radar needs at least three categories.' />
  }

  return (
    <RadarChart
      width={width}
      height={height}
      radar={{ metrics: categories }}
      series={series.map((item, index) => ({
        label: item.label,
        data: categories.map((_, point) => item.data[point] ?? 0),
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        fillArea: true,
      }))}
    />
  )
}

function FunnelView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const { categories, series } = plotSeries(chart)
  const first = series[0]
  if (!categories.length || !first) return <EmptyPlot label='No funnel data.' />

  return (
    <FunnelChart
      width={width}
      height={height}
      series={[
        {
          data: categories.map((label, index) => ({
            label,
            value: Math.abs(first.data[index] ?? 0),
          })),
        },
      ]}
    />
  )
}

function HeatmapView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const { categories, series } = plotSeries(chart)
  if (!categories.length || !series.length) return <EmptyPlot label='No heatmap data.' />

  const data = series.flatMap((item, row) =>
    categories.map((_, column) => [column, row, item.data[column] ?? 0] as [number, number, number]),
  )

  return (
    <Heatmap
      width={width}
      height={height}
      xAxis={[{ data: categories, label: chart.xLabel }]}
      yAxis={[{ data: series.map(item => item.label), label: chart.yLabel }]}
      series={[
        {
          data,
          valueFormatter: value => formatNumber(value?.[2] ?? 0),
        },
      ]}
      margin={{ left: 72, right: 12, top: 16, bottom: chart.xLabel ? 48 : 32 }}
    />
  )
}

function GaugeView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const { categories, series } = plotSeries(chart)
  const values = series[0]?.data ?? []
  if (!values.length) return <EmptyPlot label='No gauge data.' />

  let top = 0
  values.forEach((value, index) => {
    if (Math.abs(value) > Math.abs(values[top] ?? 0)) top = index
  })
  const peak = Math.abs(values[top] ?? 0)
  const ceiling = Math.max(peak, ...values.map(value => Math.abs(value)), 1)

  return (
    <Box>
      <Gauge
        width={Math.min(width, 280)}
        height={height}
        value={peak}
        valueMax={ceiling}
        startAngle={-110}
        endAngle={110}
        text={formatNumber(values[top] ?? 0)}
      />
      <Typography variant='caption' color='text.secondary' display='block' align='center'>
        {categories[top] || series[0]?.label || 'Value'}
      </Typography>
    </Box>
  )
}

function SparklineView({
  chart,
  width,
  height,
}: {
  chart: AssistantChartData
  width: number
  height: number
}) {
  const { categories, series } = plotSeries(chart)
  const first = series[0]
  if (!first?.data.length) return <EmptyPlot label='No sparkline data.' />

  return (
    <Box>
      <SparkLineChart
        data={first.data}
        width={width}
        height={height}
        plotType='line'
        area
        showTooltip
        showHighlight
        color={SERIES_COLORS[0]}
        valueFormatter={value => (value == null ? '—' : formatNumber(value))}
      />
      <Typography variant='caption' color='text.secondary' display='block'>
        {first.label}
        {categories.length > 1 ? ` · ${categories[0]} to ${categories[categories.length - 1]}` : ''}
      </Typography>
    </Box>
  )
}

interface AssistantChartListProps {
  charts: AssistantChartData[]
  onPickType?: (type: AssistantChartType) => void
  tall?: boolean
  isPinned?: (index: number) => boolean
  onTogglePin?: (index: number) => void
}

export const AssistantChartList: React.FC<AssistantChartListProps> = ({
  charts,
  onPickType,
  tall = false,
  isPinned,
  onTogglePin,
}) => (
  <Stack spacing={0}>
    {charts.map((chart, index) => (
      <AssistantChartCard
        key={`${chart.title || 'chart'}-${index}`}
        chart={chart}
        onPickType={onPickType}
        tall={tall}
        pinned={isPinned?.(index) ?? false}
        onTogglePin={onTogglePin ? () => onTogglePin(index) : undefined}
      />
    ))}
  </Stack>
)
